import React, { useEffect, useState } from "react";
import { useOrganizations } from "@/context/organizations";
import storage from "@/utils/storage";
import { useNavigate } from "react-router-dom";
import { Organization } from "@/types/utils";
import { SelectChangeEvent } from "@mui/material";

const useOrganizationsList = (data: Organization[] | undefined) => {
    const navigate = useNavigate();

    const { organizations, setOrganizations } = useOrganizations(state => state);
    const [searchValue, setSearchValue] = useState("");
    const [filteredCountry, setFilteredCountry] = useState("");
    const [filteredPlan, setFilteredPlan] = useState("");

    const [modal, setModal] = useState(false);
    const openModal = () => setModal(true);
    const closeModal = () => setModal(false);

    const onSelect = (id: string) => {
        storage.setItem("oid", id);
        navigate(`/organization/${id}`);

        if (!data) return;

        setOrganizations(data);
    };

    const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toLowerCase();
        setSearchValue(value);
        storage.setItem("organizations_filter", {
            searchValue: value,
            country: filteredCountry,
            plan: filteredPlan,
        });

        let filtered = data || [];

        if (filteredCountry) {
            filtered = filtered.filter(i =>
                i.address.country.toLowerCase().includes(filteredCountry.toLowerCase()),
            );
        }

        if (filteredPlan) {
            filtered = filtered.filter(i =>
                i.plan.toLowerCase().includes(filteredPlan.toLowerCase()),
            );
        }

        filtered = filtered.filter(i => i.title.toLowerCase().includes(value));
        setOrganizations(filtered);
    };

    const onCountrySelect = (event: SelectChangeEvent<unknown>) => {
        const value = event.target.value as string;
        setFilteredCountry(value);
        storage.setItem("organizations_filter", {
            country: value,
            plan: filteredPlan,
            searchValue: searchValue,
        });

        let filtered = data || [];

        if (filteredPlan) {
            filtered = filtered.filter(i =>
                i.plan.toLowerCase().includes(filteredPlan.toLowerCase()),
            );
        }

        filtered = filtered.filter(i =>
            i.address.country.toLowerCase().includes(value.toLowerCase()),
        );

        setOrganizations(filtered);
    };

    const onSubscriptionPlanSelect = (event: SelectChangeEvent<unknown>) => {
        const value = event.target.value as string;
        setFilteredPlan(value);
        storage.setItem("organizations_filter", {
            plan: value,
            country: filteredCountry,
            searchValue: searchValue,
        });

        let filtered = data || [];

        if (filteredCountry) {
            filtered = filtered.filter(i =>
                i.address.country.toLowerCase().includes(filteredCountry.toLowerCase()),
            );
        }

        filtered = filtered.filter(i => i.plan.toLowerCase().includes(value.toLowerCase()));

        setOrganizations(filtered);
    };

    const clearFilters = () => {
        setFilteredCountry("");
        setFilteredPlan("");
        setSearchValue("");

        storage.removeItem("organizations_filter");

        if (!data) return;

        setOrganizations(data);
    };

    useEffect(() => {
        const filter = storage.getItem("organizations_filter");
        if (filter) {
            const search = filter.searchValue || "";
            const country = filter.country || "";
            const plan = filter.plan || "";

            setSearchValue(search);
            setFilteredCountry(country);
            setFilteredPlan(plan);

            let filtered = data || [];

            if (country) {
                filtered = filtered.filter(i =>
                    i.address.country.toLowerCase().includes(country.toLowerCase()),
                );
            }

            if (plan) {
                filtered = filtered.filter(i => i.plan.toLowerCase().includes(plan.toLowerCase()));
            }

            if (search) {
                filtered = filtered.filter(i =>
                    i.title.toLowerCase().includes(search.toLowerCase()),
                );
            }

            setOrganizations(filtered);
        }
    }, [data]);

    return {
        organizations,
        modal,
        openModal,
        closeModal,
        onSelect,
        onSearch,
        onCountrySelect,
        onSubscriptionPlanSelect,
        filteredCountry,
        filteredPlan,
        clearFilters,
        searchValue,
    };
};

export { useOrganizationsList };
